<script setup lang="ts">
  import { computed, inject, ref, type Ref, onMounted, provide } from 'vue'
  import { type ClientOrganisation, type ClientQuestionnaireSubmission } from '@/types/generated'
  import { QuestionnaireSubmissionsService } from '@/types/generated'
  import { useRoute } from 'vue-router'
  import { genericErrorToast } from '@/lib/toasts'
  import { useMetrics, MetricEventType } from '@/composables/useMetrics'
  import Questionaire from '@/components/questionaire/questionnaireFormV3'
  import completedQuestionaire from '@/components/questionaire/completedQuestionaire.vue'
  import { LoaderCircle } from 'lucide-vue-next'
  import client404 from '@/components/404/404.vue'

  const route = useRoute()
  const { trackEvent } = useMetrics()
  const questionnaireUuid = route.params.uuid as string

  // Inject data from ClientPortalBase
  const accessToken = inject<string | null>('accessToken')
  const org = inject<Ref<ClientOrganisation | null>>('org')

  // Inject questionnaire ref from parent to update it
  const parentQuestionnaire = inject<Ref<any | null>>('questionnaire')
  const parentActiveField = inject<Ref<string>>('activeField')

  // Inject sidebar state from parent
  const sidebarVisible = inject<Ref<boolean>>('sidebarVisible', ref(true))
  const shouldShowSidebar = inject<Ref<boolean>>('shouldShowSidebar', ref(true))

  const loading = ref(true)
  const questionnaire = ref<ClientQuestionnaireSubmission | null>(null)

  const hasSubmission = computed(() => questionnaire.value !== null)
  const submissionIsCompleted = computed(() => questionnaire.value?.state === 'CLOSED')

  const metadata = computed(() => ({
    title: questionnaire.value?.title || '',
    totalQuestionCount: questionnaire.value?.question_count || 0,
    isPreview: false,
    isExternal: true
  }))

  const updateActiveField = (fieldId: string) => {
    if (parentActiveField) {
      parentActiveField.value = fieldId
    }
  }

  onMounted(async () => {
    try {
      if (!accessToken) {
        genericErrorToast()
        loading.value = false
        return
      }

      const response = await QuestionnaireSubmissionsService.questionnaireSubmissionsRetrieve({
        uuid: questionnaireUuid
      })

      questionnaire.value = response

      // Update parent questionnaire ref for sidebar
      if (parentQuestionnaire) {
        parentQuestionnaire.value = response
      }

      // Track questionnaire view
      if (org?.value?.uuid) {
        await trackEvent(MetricEventType.CLIENT_QUESTIONNAIRE_EDIT, {
          organization_id: org.value.uuid
        })
      }

      loading.value = false
    } catch (error) {
      console.error('Error fetching questionnaire:', error)
      genericErrorToast()
      loading.value = false
    }
  })
</script>

<template>
  <div
    v-if="loading"
    class="flex h-screen items-center justify-center"
  >
    <LoaderCircle
      :size="100"
      class="animate-spin"
    />
  </div>

  <template v-else>
    <completedQuestionaire
      v-if="submissionIsCompleted"
      :submission-url="questionnaire?.pdf_url || ''"
    />

    <div
      v-else-if="hasSubmission && questionnaire"
      class="min-h-screen w-full bg-gray-50"
    >
      <div
        class="fixed left-0 right-0 top-0 z-10 hidden flex-col gap-2 bg-gray-50 px-4 py-4 transition-all
          duration-300 ease-in-out md:flex"
        :class="shouldShowSidebar ? (sidebarVisible ? 'md:left-[250px]' : 'md:left-16') : 'left-0'"
      >
        <h2
          v-if="questionnaire.title"
          class="text-lg font-semibold text-gray-900"
        >
          {{ questionnaire.title }}
        </h2>
      </div>

      <div class="min-h-[calc(100vh-85px)] pt-14 md:pt-[85px]">
        <Questionaire
          :rawSchema="(questionnaire.schema as any[]) || []"
          :metaData="metadata"
          @active-field-change="updateActiveField"
        />
      </div>
    </div>

    <client404 v-else />
  </template>
</template>
